import {
    Chart,
    Dashboard,
    DashboardSchema,
    Group,
    KPI,
    PatchOperation
} from '@/contexts/AdminContext/dashboardProvider';
import { useAuth } from '@/contexts/AuthContext';
import { Permission } from '@/contexts/AuthContext/permissions';
import { useToast } from '@/contexts/ToastContext';
import { Tooltip } from '@common/index';
import { PenIcon } from '@phosphor-icons/react';
import {
    Check,
    LayoutTemplate,
    Trash2,
    Undo
} from 'lucide-react';
import { useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { useAdminContext } from '@/contexts/AdminContext';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';
import { runSelect } from '@/utils/run_select';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import { replaceCreatedAtFilter } from '../../utils/replaceCreatedAtFilter';
import { DateRange, DateRangePicker } from '../DateRangePicker';
import BreakdownModal from './BreakdownModal';
import { DashboardGrid } from './DashboardGrid';
import { DataSyncLoading } from './DataSyncLoading';
import styles from './index.module.scss';
import Modal from './Modal';
import modalStyles from './Modal.module.scss';
import StructureMenu from './StructureMenu';
import { GroupedLayout } from './types';
import { getPreviousRange } from './utils/dateUtils';
import { extractDataSources } from './utils/extractDataSources';
import { buildGroupedLayouts } from './utils/layoutUtils';

interface DashboardUIProps {
  schema: DashboardSchema;
  title?: string;
  createdAt?: string;
  selectedVersion?: Dashboard | null;
  isGenerating: boolean;
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
}

export const DashboardUI: React.FC<DashboardUIProps> = ({
    schema, createdAt, title = 'Dashboard', selectedVersion, showCreateModal, setShowCreateModal
}) => {
    const {
        syncStatus, user, permissions 
    } = useAuth();

    // 检查是否有 dashboard 编辑权限
    const canEditDashboard = permissions.checkPermissions([Permission.DASHBOARD_MANAGE]);
    const { showToast } = useToast();
    const {
        chat: {
            context, setContext, setIsChatVisible, isChatVisible
        },
        dashboard: {
            getDashboards, isEditMode, setIsEditMode,
            editorStates, initEditor, dispatchAction, updateData, undo, redo, saveDashboard, renameDashboard, deleteDashboard,
            activeItemId, setActiveItemId, versions, currentConversationId,
            globalDateRange, setGlobalDateRange
        }
    } = useAdminContext();
    
    // Editor State
    const dashboardId = selectedVersion?.id || 'temp';
    const editorState = editorStates[dashboardId];

    const [editingTitle, setEditingTitle] = useState(title);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);

    // Breakdown Modal State
    const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<KPI | Chart | null>(null);
    const [selectedType, setSelectedType] = useState<'kpi' | 'chart'>('kpi');

    useEffect(() => {
        setIsSwitching(true);
        const timer = setTimeout(() => {
            setIsSwitching(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [dashboardId]);
    
    useEffect(() => {
        setEditingTitle(title);
    }, [title]);

    // Initialize editor state if needed
    useEffect(() => {
        if (selectedVersion) {
            initEditor(dashboardId, selectedVersion.schema);
        }
    }, [dashboardId, selectedVersion, initEditor]);

    // Data State
    const localSchema = useMemo(() => editorState?.currentSchema || schema, [editorState, schema]);
    // console.log("localSchema", localSchema)

    const [lastUpdateTime, setLastUpdateTime] = useState<Date | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);

    // 分组布局的 state
    const [groupedLayouts, setGroupedLayouts] = useState<GroupedLayout[]>([]);
    const groupedLayoutsRef = useRef(groupedLayouts);
    groupedLayoutsRef.current = groupedLayouts;

    const dashboardRef = useRef<HTMLDivElement>(null);
    const initDoneRef = useRef(false);
    const { width: containerWidth, containerRef } = useContainerWidth({ initialWidth: 1200 });
    const renderDelayRef = useRef<number | null>(null);
    const [chartsReady, setChartsReady] = useState(false);

    // 跟踪是否正在执行撤销/重做操作
    const isUndoingRef = useRef(false);

    // 响应式列数计算 - 只保留 12 和 9 两个断点
    // 编辑模式下始终使用 12 列
    const responsiveCols = useMemo(() => {
        if (isEditMode) return 12;
        if (containerWidth >= 900) return 12;
        return 9;
    }, [containerWidth, isEditMode]);

    const isDirty = editorState?.isDirty || false;
    const currentAction = editorState?.actions[editorState.pointer];
    const isImport = currentAction?.payload.some(op => op.op === 'IMPORT');
    const canUndo = (editorState?.pointer ?? -1) >= 0 && !isImport;

    // 封装 undo 和 redo，自动处理撤销标记
    const handleUndo = useCallback((id: string) => {
        isUndoingRef.current = true;
        undo(id);
        setTimeout(() => {
            isUndoingRef.current = false;
        }, 300);
    }, [undo]);

    const handleRedo = useCallback((id: string) => {
        isUndoingRef.current = true;
        redo(id);
        setTimeout(() => {
            isUndoingRef.current = false;
        }, 300);
    }, [redo]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                e.preventDefault();
                if (e.shiftKey) {
                    handleRedo(dashboardId);
                } else {
                    handleUndo(dashboardId);
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dashboardId, handleUndo, handleRedo]);

    // 自动滚动到激活的元素（新增、更新、删除前）
    useEffect(() => {
        if (activeItemId) {
            // Give a small delay for render to complete
            setTimeout(() => {
                const element = document.getElementById(`dashboard-item-${activeItemId}`);

                if (element) {
                    // Use scrollIntoView for automatic scrolling
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });

                    // Highlight effect using CSS class instead of inline styles
                    // Add class to the wrapper
                    element.classList.add(styles.activating);

                    // Also add class to the gridItem for more reliable styling
                    // Use firstElementChild to get the gridItem div directly
                    const gridItem = element.firstElementChild;
                    if (gridItem) {
                        gridItem.classList.add(styles.activating);
                    }

                    setTimeout(() => {
                        element.classList.remove(styles.activating);
                        if (gridItem) {
                            gridItem.classList.remove(styles.activating);
                        }
                    }, 2000);
                }
                setActiveItemId(null);
            }, 500); // 1000ms delay to ensure rendering/layout is stable
        }
    }, [activeItemId, setActiveItemId]);

    // schema 变化时 更新上下文
    useEffect(() => {
        if(localSchema) {
            setContext([{
                title: title,
                payload: {
                    dashboardSchema: localSchema,
                    dateRange: globalDateRange,
                },
                type: 'dashboard',
            }]);
        }
        return () => {
            setContext([]);
        }
    }, [localSchema, globalDateRange]);

    // 初始化数据源 - 从 SQL 中提取数据源
    useEffect(() => {
        if (!localSchema) return;

        const hasSources = (schema: DashboardSchema) => {
            const kpiHasSources = (schema.kpi || []).every(kpi => kpi.sources && kpi.sources.length > 0);
            const chartHasSources = (schema.chart || []).every(chart => chart.sources && chart.sources.length > 0);
            return kpiHasSources && chartHasSources;
        };

        // 如果已经有 sources，就不再提取
        if (hasSources(localSchema)) return;

        const newSchema = JSON.parse(JSON.stringify(localSchema)) as DashboardSchema;
        let updated = false;

        // 为 KPI 提取数据源
        if (newSchema.kpi) {
            newSchema.kpi.forEach(kpi => {
                if (kpi.sql && !kpi.sources) {
                    kpi.sources = extractDataSources(kpi.sql);
                    if (kpi.sources && kpi.sources.length > 0) {
                        updated = true;
                    }
                }
            });
        }

        // 为 Chart 提取数据源
        if (newSchema.chart) {
            newSchema.chart.forEach(chart => {
                if (chart.sql && !chart.sources) {
                    chart.sources = extractDataSources(chart.sql);
                    if (chart.sources && chart.sources.length > 0) {
                        updated = true;
                    }
                }
            });
        }

        // 如果有更新，更新到 state
        if (updated) {
            updateData(dashboardId, newSchema);
        }
    }, [localSchema, dashboardId, updateData]);

    // context 被清空时，填充上下文
    useEffect(() => {
        if (context.length === 0 && localSchema) {
            setContext([{
                title: title,
                payload: {
                    dashboardSchema: localSchema,
                    dateRange: globalDateRange,
                },
                type: 'dashboard',
            }]);
        }
    }, [context, localSchema, globalDateRange]);


    const handleDateRangeChange = useCallback(async (range: DateRange) => {
        // 使用当前时间作为结束时间，而不是当天的 23:59:59
        const now = new Date();
        if (range.endDate && dayjs(range.endDate).isSame(now, 'day')) {
            // 如果选择的结束日期是今天，使用当前时间
            range.endDate = now;
        }
        setGlobalDateRange(range);
        if (!range.startDate || !range.endDate) return;

        setIsLoading(true);
        try {
            const newSchema = JSON.parse(JSON.stringify(localSchema)) as DashboardSchema;
            const promises: any[] = [];

            // Update KPIs
            if (newSchema.kpi) {
                newSchema.kpi.forEach(kpi => {
                    if (kpi.sql) {
                        const newSql = replaceCreatedAtFilter(kpi.sql, range.startDate!, range.endDate!);
                        kpi.sql = newSql;

                        const p = runSelect(newSql).then(({ data, error }) => {
                            if (!error && data) {
                                if (Array.isArray(data)) {
                                    kpi.data = data as any;
                                }
                            }else{
                                console.error("KPI fetch error:", error);
                                kpi.data = []
                            }
                        });
                        promises.push(p);

                        const prevRange = getPreviousRange(range);
                        const prevSql = replaceCreatedAtFilter(newSql, prevRange.startDate!, prevRange.endDate!);
                        const pPrev = runSelect(prevSql).then(({ data, error }) => {
                            if (!error && data) {
                                if (Array.isArray(data)) {
                                    kpi.prevData = data;
                                }
                            } else {
                                kpi.prevData = [];
                            }
                        });
                        promises.push(pPrev);
                    } else {
                        console.log("Skipping KPI fetch (no SQL):", kpi.title);
                    }
                });
            }

            // Update Charts
            if (newSchema.chart) {
                newSchema.chart.forEach(chart => {
                    if (chart.sql) {
                        const newSql = replaceCreatedAtFilter(chart.sql, range.startDate!, range.endDate!);
                        chart.sql = newSql;

                        const p = runSelect(newSql).then(({ data, error }) => {
                            if (!error && data) {
                                if (Array.isArray(data)) {
                                    chart.data = data as any;
                                }
                            }else{
                                console.error("Chart fetch error:", error);
                                chart.data = []
                            }
                        });
                        promises.push(p);
                    } else {
                        console.log("Skipping Chart fetch (no SQL):", chart.title);
                    }
                });
            }

            await Promise.all(promises);
            updateData(dashboardId, newSchema);

            // 更新时间设置为当前时间
            setLastUpdateTime(now);
        } finally {
            setIsLoading(false);
        }
    }, [localSchema, dashboardId, updateData, setGlobalDateRange]);

    // 自动刷新 - 每5秒调用一次 handleDateRangeChange，第一次不调用
    useAutoRefresh(
        handleDateRangeChange,
        globalDateRange,
        {
            interval: 5 * 60000,
            enabled: !isEditMode && !!globalDateRange?.startDate && !!globalDateRange?.endDate
        }
    );

    // schema 变化初始化数据
    useEffect(() => {
        if(!user) return;
        if (!syncStatus.syncCompleted) return;
        if(isEditMode) return;
        if (initDoneRef.current) return;
        if (!localSchema || (!localSchema.kpi && !localSchema.chart)) return;
        if (!globalDateRange?.startDate || !globalDateRange?.endDate) return;

        const start = dayjs(globalDateRange.startDate).startOf('day');
        const end = dayjs(globalDateRange.endDate).endOf('day');
        const days = Math.max(0, end.diff(start, 'day'));

        const now = dayjs();
        const newEnd = now.toDate();
        const newStart = now.subtract(days, 'day').toDate();

        initDoneRef.current = true;
        handleDateRangeChange({ startDate: newStart, endDate: newEnd });
    }, [localSchema, globalDateRange, syncStatus.syncCompleted, isEditMode, user, handleDateRangeChange]);

    // 生成分组布局
    const currentGroupedLayouts = useMemo(
        () => buildGroupedLayouts(localSchema, responsiveCols),
        [localSchema, responsiveCols]
    );

    // Sync layouts when schema changes
    useEffect(() => {
        // 撤销/重做期间跳过布局同步
        if (isUndoingRef.current) return;

        if (isEditMode) {
            // 只在结构变化时更新布局
            const currentIds = new Set(groupedLayoutsRef.current.flatMap(g => g.items.map(i => i.key)));
            const newIds = new Set(currentGroupedLayouts.flatMap(g => g.items.map(i => i.key)));

            let hasStructuralChange = currentIds.size !== newIds.size;
            if (!hasStructuralChange) {
                for (const id of newIds) {
                    if (!currentIds.has(id)) {
                        hasStructuralChange = true;
                        break;
                    }
                }
            }

            if (hasStructuralChange) {
                setGroupedLayouts(currentGroupedLayouts);
            }
        } else {
            setGroupedLayouts(currentGroupedLayouts);
        }
    }, [currentGroupedLayouts, isEditMode]);

    // When switching to edit mode or responsiveCols changes, reset layout
    useEffect(() => {
        if (isEditMode) {
            // 切换到编辑模式时，重置为当前列数的布局
            setGroupedLayouts(currentGroupedLayouts);
        }
    }, [isEditMode, responsiveCols, currentGroupedLayouts]);

    useEffect(() => {
        setChartsReady(false);
        if (renderDelayRef.current !== null) {
            clearTimeout(renderDelayRef.current);
        }
        renderDelayRef.current = window.setTimeout(() => setChartsReady(true), 200);
        return () => {
            if (renderDelayRef.current !== null) {
                clearTimeout(renderDelayRef.current);
            }
        };
    }, [containerWidth, groupedLayouts, isEditMode]);

    const handleAskAi = (type: 'kpi' | 'chart', data: KPI | Chart) => {
        const partialSchema: DashboardSchema = {
            kpi: type === 'kpi' ? [data as KPI] : [],
            chart: type === 'chart' ? [data as Chart] : [],
        };

        setContext([{
            title: data.title,
            payload: {
                dashboardSchema: partialSchema,
                dateRange: {
                    startDate: dayjs(globalDateRange?.startDate).tz(dayjs.tz.guess()).format(),
                    endDate: dayjs(globalDateRange?.endDate).tz(dayjs.tz.guess()).format(),
                },
            },
            canDelete: true,
            type: 'dashboard',
        }]);
        setIsChatVisible(true);
    };

    const openBreakdown = (type: 'kpi' | 'chart', data: KPI | Chart) => {
        setSelectedType(type);
        setSelectedItem(data);
        setBreakdownModalOpen(true);
    };

    const closeBreakdown = () => {
        setBreakdownModalOpen(false);
        setSelectedItem(null);
    };

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
    };

    const handleSave = async () => {
        setIsLoading(true);
        try {
            // 使用 currentConversationId，它会自动从 temp 更新为真实的 conversation ID
            // 只有当 conversationId 不是 'temp' 时才关联
            const conversationId = currentConversationId && currentConversationId !== 'temp'
                ? currentConversationId
                : undefined;

            // saveDashboard now handles both create (if isTemp) and update
            await saveDashboard(dashboardId, editingTitle, conversationId);

            showToast('Dashboard saved successfully!', 'success');
            setIsEditMode(false);

        } catch (error) {
            console.error('Error saving dashboard:', error);
            showToast('Failed to save dashboard', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // 处理分组重排序
    const handleGroupReorder = useCallback((newGroups: Group[]) => {
        const ops: PatchOperation[] = [];

        // 直接替换整个 group 数组
        ops.push({
            op: 'UPDATE',
            path: 'group',
            value: newGroups
        });

        dispatchAction(dashboardId, {
            type: 'BATCH_PATCH',
            payload: ops,
            description: 'Reorder groups'
        });
    }, [dashboardId, dispatchAction]);

    // 滚动到指定分组
    const handleScrollToGroup = useCallback((groupId: string) => {
        const element = document.getElementById(`group-${groupId}`);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, []);

    // Resizing state for visual feedback
    // Note: We use pure DOM manipulation for visual feedback during resize to avoid re-renders
    // that would cause react-grid-layout to lose its internal state and reset item position.

    const onResizeStart = (layout: any, oldItem: any, newItem: any) => {
        if (newItem) {
            // Add resizing class to the item container
            // The item key corresponds to the div id inside GridLayout if we set it, 
            // but here we are mapping children. We can find the element by data-key or id if available.
            // React-grid-layout wraps children in a div.react-grid-item.
            
            // We'll use a more direct approach: modify styles via the event element if possible, 
            // but RGL doesn't pass the element directly in onResizeStart signature here.
            // So we rely on the badge ID we set in the render loop.
            const badge = document.getElementById(`resize-badge-${newItem.i}`);
            if (badge) {
                // Update text
                badge.innerText = `${newItem.w} × ${newItem.h}`;
                
                // Find the parent grid item to add the .resizing class
                // The structure is: .gridItem -> .resizeOverlay -> .resizeBadge
                // So badge.parentElement is overlay, overlay.parentElement is gridItem
                const overlay = badge.parentElement;
                if (overlay) {
                    overlay.classList.add(styles.visible);
                    const gridItem = overlay.parentElement;
                    if (gridItem) {
                        gridItem.classList.add(styles.resizing);
                    }
                }
            }
        }
    };

    const onResize = (layout: any, oldItem: any, newItem: any) => {
        if (!newItem) return;
        const badge = document.getElementById(`resize-badge-${newItem.i}`);
        if (badge) {
            badge.innerText = `${newItem.w} × ${newItem.h}`;
        }
    };

    const onResizeStop = (layout: any, oldItem: any, newItem: any) => {
        if (!newItem) return;
        const badge = document.getElementById(`resize-badge-${newItem.i}`);
        if (badge) {
            const overlay = badge.parentElement;
            if (overlay) {
                overlay.classList.remove(styles.visible);
                const gridItem = overlay.parentElement;
                if (gridItem) {
                    gridItem.classList.remove(styles.resizing);
                }
            }
        }
    };

    // Dragging state for visual feedback
    const [draggingId, setDraggingId] = useState<string | null>(null);

    const onDragStart = (layout: any, oldItem: any, newItem: any) => {
        setDraggingId(newItem.i);
    };

    const onDragStop = (layout: any, oldItem: any, newItem: any) => {
        setDraggingId(null);
    };

    const handleDelete = (key: string) => {
        
        const ops: PatchOperation[] = [];
        
        // Find and delete from KPI array
        const kpiIndex = (localSchema.kpi || []).findIndex((k, idx) => (k.id || `kpi-${idx}`) === key);
        if (kpiIndex > -1) {
            ops.push({
                op: 'DELETE',
                path: `kpi[${kpiIndex}]`
            });
        }

        // Find and delete from Chart array
        const chartIndex = (localSchema.chart || []).findIndex((c, idx) => (c.id || `chart-${idx}`) === key);
        if (chartIndex > -1) {
            ops.push({
                op: 'DELETE',
                path: `chart[${chartIndex}]`
            });
        }

        // Remove from groups if exists
        (localSchema.group || []).forEach((group, groupIndex) => {
            if (group.items_id?.includes(key)) {
                ops.push({
                    op: 'UPDATE',
                    path: `group[${groupIndex}].items_id`,
                    value: group.items_id.filter(id => id !== key)
                });
            }
        });

        if (ops.length > 0) {
            dispatchAction(dashboardId, {
                type: 'BATCH_PATCH',
                payload: ops,
                description: 'Delete item'
            });
        }
    };

    // 渲染分组标题
    const renderGroupHeader = (groupData: Group, groupIndex: number) => {
        if (!isEditMode) {
            return (
                <div className={styles.groupHeader}>
                    <h2 className={styles.groupTitle}>{groupData.title}</h2>
                </div>
            );
        }

        return (
            <div className={styles.groupHeaderEdit}>
                <div className={styles.groupTitleEditWrapper}>
                    <input
                        type="text"
                        value={groupData.title}
                        onChange={(e) => {
                            const ops: PatchOperation[] = [];
                            const groupIndex = (localSchema.group || []).findIndex(g => g.id === groupData.id);
                            if (groupIndex > -1) {
                                ops.push({
                                    op: 'UPDATE',
                                    path: `group[${groupIndex}].title`,
                                    value: e.target.value
                                });
                                dispatchAction(dashboardId, {
                                    type: 'BATCH_PATCH',
                                    payload: ops,
                                    description: 'Rename group'
                                });
                            }
                        }}
                        className={styles.groupTitleInput}
                    />
                    <button
                        onClick={() => {
                            const ops: PatchOperation[] = [];
                            const groupIndex = (localSchema.group || []).findIndex(g => g.id === groupData.id);
                            if (groupIndex > -1) {
                                // 先删除分组中的所有 items
                                const group = localSchema.group![groupIndex];
                                const kpiIndexes: number[] = [];
                                const chartIndexes: number[] = [];

                                (group.items_id || []).forEach((itemId) => {
                                    // 收集要删除的 kpi 索引
                                    const kpiIndex = (localSchema.kpi || []).findIndex((k, idx) => (k.id || `kpi-${idx}`) === itemId);
                                    if (kpiIndex > -1) {
                                        kpiIndexes.push(kpiIndex);
                                    }

                                    // 收集要删除的 chart 索引
                                    const chartIndex = (localSchema.chart || []).findIndex((c, idx) => (c.id || `chart-${idx}`) === itemId);
                                    if (chartIndex > -1) {
                                        chartIndexes.push(chartIndex);
                                    }
                                });

                                // 按索引从大到小排序，避免删除时索引变化
                                kpiIndexes.sort((a, b) => b - a).forEach(idx => {
                                    ops.push({ op: 'DELETE', path: `kpi[${idx}]` });
                                });
                                chartIndexes.sort((a, b) => b - a).forEach(idx => {
                                    ops.push({ op: 'DELETE', path: `chart[${idx}]` });
                                });

                                // 最后删除分组本身
                                ops.push({
                                    op: 'DELETE',
                                    path: `group[${groupIndex}]`
                                });

                                // 清空所有 layout，让系统重新计算紧凑布局
                                (localSchema.kpi || []).forEach((kpi, idx) => {
                                    if (kpi.layout) {
                                        ops.push({
                                            op: 'UPDATE',
                                            path: `kpi[${idx}].layout`,
                                            value: { w: kpi.layout.w, h: kpi.layout.h }
                                        });
                                    }
                                });

                                (localSchema.chart || []).forEach((chart, idx) => {
                                    if (chart.layout) {
                                        ops.push({
                                            op: 'UPDATE',
                                            path: `chart[${idx}].layout`,
                                            value: { w: chart.layout.w, h: chart.layout.h }
                                        });
                                    }
                                });

                                dispatchAction(dashboardId, {
                                    type: 'BATCH_PATCH',
                                    payload: ops,
                                    description: 'Delete group and its items'
                                });
                            }
                        }}
                        className={styles.groupDeleteButton}
                        title="Delete Group"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>
        );
    };

    // 渲染单个分组的网格
    const renderGroupGrid = (groupedLayout: GroupedLayout, groupIndex: number) => {
        const {
            group, items: groupItems, layout: groupLayout, hidden
        } = groupedLayout;

        return (
            <div
                key={group.id}
                id={`group-${group.id}`}
                className={clsx(
                    styles.dashboardGroup,
                    isEditMode && styles.groupEditMode,
                    hidden && styles.hiddenGroup
                )}
                style={{marginTop: 16,}}
            >
                {!hidden && renderGroupHeader(group, groupIndex)}

                <div className={styles.groupContent}>
                    <DashboardGrid
                        className={`group-layout-${group.id}`}
                        layout={groupLayout}
                        items={groupItems}
                        responsiveCols={responsiveCols}
                        containerWidth={containerWidth}
                        isEditMode={isEditMode}
                        isLoading={isLoading}
                        draggingId={draggingId}
                        chartsReady={chartsReady}
                        onLayoutChange={(newLayout) => {
                            if (!isEditMode || responsiveCols !== 12) return;

                            // 撤销/重做期间跳过布局变化处理
                            if (isUndoingRef.current) return;

                            // 保存该分组内元素的布局
                            const ops: PatchOperation[] = [];
                            const newLayoutMap = new Map(newLayout.map(l => [l.i, l]));

                            // 检查新布局和 schema 中的布局是否一致（避免重复触发）
                            let hasRealChange = false;

                            groupItems.forEach((item) => {
                                const newLayoutItem = newLayoutMap.get(item.key);
                                if (newLayoutItem) {
                                    // 找到对应的 KPI 或 Chart 及其索引
                                    const kpiIndex = (localSchema.kpi || []).findIndex((k, idx) => (k.id || `kpi-${idx}`) === item.key);
                                    const chartIndex = (localSchema.chart || []).findIndex((c, idx) => (c.id || `chart-${idx}`) === item.key);

                                    const kpi = kpiIndex > -1 ? localSchema.kpi?.[kpiIndex] : undefined;
                                    const chart = chartIndex > -1 ? localSchema.chart?.[chartIndex] : undefined;

                                    const storedLayout = kpi?.layout || chart?.layout;

                                    // 比较 schema 中存储的布局和新布局
                                    if (storedLayout) {
                                        if (storedLayout.x !== newLayoutItem.x
                                                || storedLayout.y !== newLayoutItem.y
                                                || storedLayout.w !== newLayoutItem.w
                                                || storedLayout.h !== newLayoutItem.h) {
                                            hasRealChange = true;
                                        }
                                    } else {
                                        // schema 中没有布局信息，说明是新的变化
                                        hasRealChange = true;
                                    }

                                    if (kpi && kpiIndex > -1) {
                                        ops.push({
                                            op: 'UPDATE',
                                            path: `kpi[${kpiIndex}].layout`,
                                            value: {
                                                x: newLayoutItem.x,
                                                y: newLayoutItem.y,
                                                w: newLayoutItem.w,
                                                h: newLayoutItem.h
                                            }
                                        });
                                    } else if (chart && chartIndex > -1) {
                                        ops.push({
                                            op: 'UPDATE',
                                            path: `chart[${chartIndex}].layout`,
                                            value: {
                                                x: newLayoutItem.x,
                                                y: newLayoutItem.y,
                                                w: newLayoutItem.w,
                                                h: newLayoutItem.h
                                            }
                                        });
                                    }
                                }
                            });

                            if (!hasRealChange || ops.length === 0) return;

                            dispatchAction(dashboardId, {
                                type: 'BATCH_PATCH',
                                payload: ops,
                                description: 'Group layout changed',
                                skipUndo: false // 系统自动调整的布局，撤销时应该跳过
                            });
                        }}
                        onResizeStart={onResizeStart}
                        onResize={onResize}
                        onResizeStop={onResizeStop}
                        onDragStart={onDragStart}
                        onDragStop={onDragStop}
                        handleDelete={handleDelete}
                        openBreakdown={openBreakdown}
                        handleAskAi={handleAskAi}
                    />
                </div>
            </div>
        );
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditingTitle(e.target.value);
    };

    return (
        <div className={clsx(styles.dashboard, isEditMode && styles.editMode)} ref={dashboardRef}>
            <div className={styles.header}>
                <div className={styles.title}>
                    {!isEditMode && (
                        <div
                            style={{
                                display: 'flex', alignItems: 'center', gap: '12px' 
                            }}
                        >
                            <h1>{title}</h1>
                            {lastUpdateTime ? (
                                <span
                                    style={{
                                        fontSize: '12px',
                                        color: '#9CA3AF',
                                        fontWeight: 500,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Last refreshed:
                                    {' '}
                                    {dayjs(lastUpdateTime).format('h:mm A')}
                                    {' '}
                                    (
                                    {Intl.DateTimeFormat().resolvedOptions().timeZone}
                                    {' '}
                                    Time)
                                </span>
                            ) : null}
                        </div>
                    )}
                </div>
                <div className={styles.actions}>
                    {!isEditMode && (
                    <>
                        {/* Edit Button */}
                        {canEditDashboard && syncStatus.syncCompleted ? (
                            <Tooltip content="Edit Dashboard">
                                <button
                                    onClick={toggleEditMode}
                                    className={`${styles.iconEditBtn} group`}
                                >
                                    <PenIcon size={20} weight="bold" className="text-[#1A162F]" />
                                    <div className={styles.editTooltip}>
                                        Edit Dashboard
                                    </div>
                                </button>
                            </Tooltip>
                        ) : null}

                        {/* Date Range Picker */}
                        {globalDateRange && syncStatus.syncCompleted ? (
                            <DateRangePicker
                                value={globalDateRange}
                                onConfirm={handleDateRangeChange}
                                className={styles.dateTrigger}
                            />
                        ) : null}
                    </>
                    )}

                    {isEditMode ? (() => {
                        const container = document.getElementById('admin-edit-header-content');
                        return container ? createPortal(
                            <div
                                className={styles.header}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <div className={styles.title}>
                                    <input
                                        type="text"
                                        value={editingTitle}
                                        className={styles.editTitleInput}
                                        placeholder="Dashboard Name"
                                        onChange={handleTitleChange}
                                    />
                                    <span className={styles.editingBadge}>EDITING</span>
                                </div>
                                <div className={styles.actions}>
                                    <button
                                        onClick={() => setIsDeleteModalOpen(true)}
                                        className={`${styles.headerBtn} ${styles.undo}`}
                                        title="Delete Dashboard"
                                        style={{ color: '#ef4444' }}
                                    >
                                        <Trash2 size={16} />
                                    </button>

                                    {/* Structure Menu - 只在有真实分组（非隐藏）时显示 */}
                                    {groupedLayouts.some(g => !g.hidden) ? (
                                        <StructureMenu
                                            groups={localSchema.group || []}
                                            onReorder={handleGroupReorder}
                                            onScrollTo={handleScrollToGroup}
                                        />
                                    ) : null}

                                    {/* Undo Button */}
                                    <button
                                        onClick={() => handleUndo(dashboardId)}
                                        disabled={!canUndo}
                                        title="Undo (Cmd+Z)"
                                        className={`${styles.headerBtn} ${styles.undo}`}
                                    >
                                        <Undo size={16} />
                                    </button>

                                    {/* Save Button */}
                                    <button
                                        onClick={handleSave}
                                        disabled={dashboardId !== 'temp_dashboard' && !isDirty && editingTitle === title}
                                        className={`${styles.headerBtn} ${styles.save}`}
                                    >
                                        <Check size={16} />
                                        <span>Publish</span>
                                    </button>
                                </div>
                            </div>,
                            container
                        ) : null;
                    })() : null}
                </div>
            </div>
                
            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                title="Delete Dashboard"
            >
                <div className={modalStyles.modalBody}>
                    <p className={modalStyles.modalText}>
                        Are you sure you want to delete 
                        {' '}
                        <span className={modalStyles.modalStrong}>
                            "
                            {title}
                            "
                        </span>
                        ? This action cannot be undone.
                    </p>
                    <div className={modalStyles.modalFooter}>
                        <button
                            onClick={() => setIsDeleteModalOpen(false)}
                            className={modalStyles.modalCancelBtn}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={async () => {
                                await deleteDashboard(dashboardId);
                                setIsDeleteModalOpen(false);
                                setIsEditMode(false);
                            }}
                            className={modalStyles.modalDeleteBtn}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Data Sync Loading State */}
            {!syncStatus.syncCompleted && <DataSyncLoading />}

            <div
                ref={containerRef as any}
                className={clsx(
                    isEditMode && styles.editMode,
                    isSwitching && styles.disableAnimation
                )}
                style={!syncStatus.syncCompleted ? { display: 'none' } : {}}
            >
                {/* 统一使用分组布局渲染 */}
                {groupedLayouts.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyStateIconWrapper}>
                            <LayoutTemplate size={32} />
                        </div>
                        <div className={styles.emptyStateContent}>
                            <h3>Start Building</h3>
                            <p>Ask Bustly AI to create your first metric</p>
                        </div>
                    </div>
                ) : (
                    groupedLayouts.map((groupedLayout, index) => renderGroupGrid(groupedLayout, index))
                )}
            </div>

            {/* Breakdown Modal */}
            <BreakdownModal
                isOpen={breakdownModalOpen}
                onClose={closeBreakdown}
                item={selectedItem}
                type={selectedType}
                dateRange={globalDateRange}
            />
        </div>
    );
};
